import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { WithdrawalRequest, WithdrawalFilters, Pagination } from '../../../types';
import { useProviderFeeConfigs, useWithdrawals } from '../../../hooks/useApi';
import { usePermission } from '../../../hooks/useAuth';
import { enrichWithdrawalsWithPortalUsdFees } from '../../../utils/portalUsdWithdrawalDisplay';
import WithdrawalList from './WithdrawalList';
import WithdrawalDetails from './WithdrawalDetails';
import ApproveWithdrawalModal from './ApproveWithdrawalModal';
import RejectWithdrawalModal from './RejectWithdrawalModal';
import ProfitAnalysisPanel from '../flaggedWithdrawals/ProfitAnalysisPanel';
import CompleteRecoveryModal from './CompleteRecoveryModal';
import { PageHeader } from '@kioskgaming/ui';
import { toast } from 'react-hot-toast';
import { isWithdrawalUnderReview } from '../../../utils/withdrawalFlagDisplay';

const WithdrawalManagement: React.FC = () => {
  const [searchParams] = useSearchParams();
  const canApproveWithdrawal = usePermission('withdrawalManagement');
  const urlTx = searchParams.get('tx') || searchParams.get('search') || '';

  const [filters, setFilters] = useState<WithdrawalFilters>({
    page: 1,
    limit: 20,
    dateFrom: '',
    dateTo: '',
    search: urlTx || undefined,
  });

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      search: urlTx || undefined,
      page: 1,
    }));
  }, [urlTx]);
  const [selectedWithdrawal, setSelectedWithdrawal] = useState<WithdrawalRequest | null>(null);
  const [showWithdrawalDetails, setShowWithdrawalDetails] = useState(false);
  const [showProfitAnalysis, setShowProfitAnalysis] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showRecoveryModal, setShowRecoveryModal] = useState(false);

  const { data: withdrawalsData, isLoading, isFetching, error, refetch } = useWithdrawals(filters);
  const { data: providerFeesData } = useProviderFeeConfigs();
  const providerFeeItems = providerFeesData?.data?.items ?? [];

  const withdrawals = useMemo(
    () => enrichWithdrawalsWithPortalUsdFees(withdrawalsData?.data?.withdrawals || [], providerFeeItems),
    [withdrawalsData, providerFeeItems]
  );
  const activeWithdrawal = useMemo(() => {
    if (!selectedWithdrawal?.transactionId) return selectedWithdrawal;
    return (
      withdrawals.find((w) => w.transactionId === selectedWithdrawal.transactionId) ?? selectedWithdrawal
    );
  }, [withdrawals, selectedWithdrawal]);
  const pagination: Pagination = withdrawalsData?.data?.pagination || {
    page: 1,
    limit: 20,
    total: 0,
    pages: 1,
  };

  const handleViewWithdrawal = (withdrawal: WithdrawalRequest) => {
    setSelectedWithdrawal(withdrawal);
    setShowWithdrawalDetails(true);
  };

  const handleCloseWithdrawalDetails = () => {
    setShowWithdrawalDetails(false);
    if (!showProfitAnalysis) {
      setSelectedWithdrawal(null);
    }
  };

  const handleViewReviewAnalysis = () => {
    if (!selectedWithdrawal || !isWithdrawalUnderReview(selectedWithdrawal)) return;
    setShowWithdrawalDetails(false);
    setShowProfitAnalysis(true);
  };

  const handleCloseProfitAnalysis = () => {
    setShowProfitAnalysis(false);
    setSelectedWithdrawal(null);
  };

  const handleApproveWithdrawal = (withdrawal: WithdrawalRequest) => {
    if (withdrawal.user?.shop_id || withdrawal.user?.shopId) {
      toast.error('Shop player: admin can only view, cannot approve/reject.');
      return;
    }
    setSelectedWithdrawal(withdrawal);
    setShowApproveModal(true);
  };

  const handleRejectWithdrawal = (withdrawal: WithdrawalRequest) => {
    if (withdrawal.user?.shop_id || withdrawal.user?.shopId) {
      toast.error('Shop player: admin can only view, cannot approve/reject.');
      return;
    }
    setSelectedWithdrawal(withdrawal);
    setShowRejectModal(true);
  };

  const handleRecoverWithdrawal = (withdrawal: WithdrawalRequest) => {
    setSelectedWithdrawal(withdrawal);
    setShowRecoveryModal(true);
  };

  const handleWithdrawalUpdate = () => {
    // Refresh the data
    refetch();
  };

  const handleCloseModals = () => {
    setShowApproveModal(false);
    setShowRejectModal(false);
    setShowRecoveryModal(false);
    setSelectedWithdrawal(null);
  };

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-destructive">Unable to load withdrawal requests</p>
          <button
            onClick={() => refetch()}
            className="mt-2 text-sm text-primary hover:underline"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Withdrawal Requests"
        description="Review payout requests, verify requester ownership, and take approval actions when allowed."
      />

      {/* Withdrawal List */}
      <WithdrawalList
        withdrawals={withdrawals}
        pagination={pagination}
        isLoading={isLoading}
        isFetching={isFetching}
        appliedSort={{
          sortBy: pagination.sortBy ?? null,
          sortOrder: pagination.sortOrder ?? null,
        }}
        filters={filters}
        onFiltersChange={setFilters}
        onViewWithdrawal={handleViewWithdrawal}
        onApproveWithdrawal={handleApproveWithdrawal}
        onRejectWithdrawal={handleRejectWithdrawal}
        onRecoverWithdrawal={handleRecoverWithdrawal}
        onRefresh={refetch}
        canApproveWithdrawal={canApproveWithdrawal}
      />

      {/* Modals */}
      {showWithdrawalDetails && activeWithdrawal && (
        <WithdrawalDetails
          withdrawal={activeWithdrawal}
          onClose={handleCloseWithdrawalDetails}
          onSuccess={handleWithdrawalUpdate}
          onViewReviewAnalysis={
            isWithdrawalUnderReview(activeWithdrawal) ? handleViewReviewAnalysis : undefined
          }
        />
      )}

      {showProfitAnalysis && activeWithdrawal && (
        <ProfitAnalysisPanel
          withdrawal={activeWithdrawal}
          onClose={handleCloseProfitAnalysis}
          onApprove={() => {
            handleCloseProfitAnalysis();
            handleApproveWithdrawal(activeWithdrawal);
          }}
          onReject={() => {
            handleCloseProfitAnalysis();
            handleRejectWithdrawal(activeWithdrawal);
          }}
          onAdjustBalance={handleCloseProfitAnalysis}
        />
      )}

      <ApproveWithdrawalModal
        withdrawal={activeWithdrawal}
        isOpen={showApproveModal}
        onClose={handleCloseModals}
        onSuccess={handleWithdrawalUpdate}
      />

      <RejectWithdrawalModal
        withdrawal={activeWithdrawal}
        isOpen={showRejectModal}
        onClose={handleCloseModals}
        onSuccess={handleWithdrawalUpdate}
      />

      <CompleteRecoveryModal
        withdrawal={activeWithdrawal}
        isOpen={showRecoveryModal}
        onClose={handleCloseModals}
        onSuccess={handleWithdrawalUpdate}
      />
    </div>
  );
};

export default WithdrawalManagement;
